import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import closeIcon from 'assets/images/icons/clear-icon.svg';
import Icon from 'components/Icon';
import Button from 'components/Button/new';
import MarketSwitch from 'components/MarketSwitch';
import LoadingMessage from 'components/LoadingMessage';
import SearchInputSuggestion from 'components/SearchInput/SearchInputSuggestion';
import { getSuggestionParsed } from 'components/SearchInput/getSuggestionParsed';
import { colors } from 'constants/colors';
import { I18N_PLATFORM_COMMON_WORD_PATH } from 'constants/i18n';
import { INPUT_DEBOUNCE_TIME_MS } from 'constants/inputs';
import { Keyboard } from 'constants/keyboard';
import { DASH_PLACEHOLDER } from 'constants/placeholders';
import { SPECIAL_CHARS } from 'constants/regex';
import { authContext } from 'contexts/AuthContext';
import { ISearchInputCustomOption } from 'interfaces/ISearchInputCustomOption';
import { IBoundarySuggestionInputs } from 'interfaces/IBoundarySuggestionInputs';
import { IPropertyCompany } from 'interfaces/IPropertyCompany';
import { IdName } from 'interfaces/IdName';
import { IPropertiesCompaniesSearchResult } from 'interfaces/IPropertiesCompaniesSearch';
import { useUserMarkets } from 'hooks/useUserMarkets';
import { convertIMarketIntoIdName } from 'utils/markets';
import { translateText } from 'utils/i18n';

import {
  IQuerySearchInput,
  ResultParserFn,
  SearchResultDataType,
} from './interfaces';
import { SearchInputSize, SuggestionIcon } from './enums';
import styles from './SearchInput.module.scss';

const PAGE_SIZE = 50;
const MIN_CHARS_FOR_SEARCH = 3;

export type SearchInputPropTypes = {
  aliasPlaceholder?: (suggestionName: string) => string;
  autofocus?: boolean;
  clearAfterSuggestionClick?: boolean;
  customOption?: ISearchInputCustomOption;
  filterSuggestions?: (data: SearchResultDataType[]) => SearchResultDataType[];
  graphqlParams: IQuerySearchInput;
  hasBottomMargin?: boolean;
  hideEmptySuggestionsPressEnter?: boolean;
  isSearchIconLeftVisible?: boolean;
  initialValue?: string;
  inputId?: string;
  isDisabled?: boolean;
  isEditable?: boolean;
  navbar?: boolean;
  onClickSuggestion: (value: SearchResultDataType) => void;
  placeholder?: string;
  resultSelectedComponent?: any;
  selectable?: boolean;
  suggestionIconType?: SuggestionIcon;
  onBlur?: () => void;
  onChangeText?: (value?: string) => void;
  onClearSelectedSuggestion?: () => void;
  onFocus?: () => void;
  parseResults?: ResultParserFn;
  size?: SearchInputSize;
  withMarketSwitch?: boolean;
  wrapperClassName?: string;
  suggestionClassName?: string;
  inputContainerClassName?: string;
  includeMapBoxResults?: boolean;
  inputBottomChildren?: React.ReactNode;
  fetchGlobalBoundaries?: boolean;
  suggestGlobalBoundaries?: (
    apiUrl: string,
    requestData: IBoundarySuggestionInputs,
  ) => Promise<any[]>;
  selectedCountry?: string;
  globalBoundaries?: boolean;
  parseMapboxResult?: (v: SearchResultDataType[]) => IPropertyCompany[];
};

const SearchInput: React.FC<SearchInputPropTypes> = ({
  aliasPlaceholder,
  onClickSuggestion,
  onChangeText,
  placeholder,
  selectable,
  navbar,
  autofocus,
  graphqlParams,
  isSearchIconLeftVisible,
  inputId,
  isDisabled,
  isEditable,
  filterSuggestions,
  suggestionIconType,
  inputBottomChildren,
  initialValue,
  resultSelectedComponent,
  hasBottomMargin,
  onClearSelectedSuggestion,
  parseResults,
  onFocus,
  onBlur,
  customOption,
  hideEmptySuggestionsPressEnter,
  withMarketSwitch,
  size = SearchInputSize.Medium,
  clearAfterSuggestionClick,
  wrapperClassName,
  inputContainerClassName,
  suggestionClassName,
  includeMapBoxResults,
  suggestGlobalBoundaries,
  fetchGlobalBoundaries,
  selectedCountry,
  globalBoundaries,
  parseMapboxResult,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const marketSwitchContainerRef = useRef<HTMLDivElement>(null);

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<
    number | null
  >(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isInputFocused, setInputIsFocused] = useState(autofocus);
  const [searchInputText, setSearchInputText] = useState(initialValue || '');
  const [searchTerm, setSearchTerm] = useState(initialValue || '');
  const [showErrorCharacter, setShowErrorCharacter] = useState(false);
  const [skipQuery, setSkipQuery] = useState(false);
  const [clearButtonMarginRight, setClearButtonMarginRight] = useState(0);
  const [marketsFilter, setMarketsFilter] = useState<IdName[]>([]);
  const [additionalData, setAdditionalData] = useState<SearchResultDataType[]>(
    [],
  );
  const [combinedResults, setCombinedResults] = useState<
    SearchResultDataType[]
  >([]);

  const { pathname } = useLocation();
  const { user } = React.useContext(authContext);
  const { userMarkets } = useUserMarkets(user);

  const debouncedSearchTerm = useRef(
    debounce(setSearchTerm, INPUT_DEBOUNCE_TIME_MS),
  );

  useEffect(() => {
    setMarketsFilter(userMarkets.map(convertIMarketIntoIdName));
  }, [userMarkets]);

  useEffect(() => {
    const debounceFn = debouncedSearchTerm.current;
    debounceFn(searchInputText);

    return () => {
      debounceFn.cancel?.();
    };
  }, [searchInputText]);

  useLayoutEffect(() => {
    // the horizontal placement of the "clear search" button changes
    // depending on the width of the market switch, if present
    if (withMarketSwitch && marketSwitchContainerRef.current) {
      const marginRight = marketSwitchContainerRef.current.clientWidth;
      setClearButtonMarginRight(marginRight);
    }
  }, [withMarketSwitch, marketsFilter, showSuggestions]);

  const {
    filterBySearchField,
    field,
    fieldOr,
    query,
    resultDataName,
    onlyCompanies,
    onlyProperties,
    onlyCities,
    withSubmarkets,
    withMarkets,
    owners,
  } = graphqlParams;

  const globalBoundariesSuggestionsUrl =
    window._env_.GLOBAL_BOUNDARIES_SUGGESTIONS_API_URL;

  const getFilterQuery = () => {
    const applyMarketFilter = withMarketSwitch && !!marketsFilter.length;
    const withOwnersFilter = owners ? { owners } : undefined;

    if (filterBySearchField) {
      const orField = fieldOr
        ? {
            key: fieldOr,
            op: 'ilike',
            value: searchTerm,
          }
        : {};

      const marketIds = applyMarketFilter
        ? { markets: marketsFilter.map(m => ({ id: m.id })) }
        : undefined;

      return {
        ...marketIds,
        ...withOwnersFilter,
        fields: {
          or: [
            {
              key: field,
              op: 'ilike',
              value: searchTerm,
            },
            orField,
          ],
        },
      };
    }

    const onlyCompaniesFilter = onlyCompanies
      ? { onlyCompanies: true }
      : undefined;

    const onlyPropertiesFilter = onlyProperties
      ? { onlyProperties: true }
      : undefined;

    const onlyCitiesFilter = onlyCities ? { onlyCities: true } : undefined;

    const withSubmarketsFilter = withSubmarkets
      ? { withSubmarkets: true }
      : undefined;

    const withMarketsFilter = withMarkets ? { withMarkets: true } : undefined;

    const withMapBoxResults = includeMapBoxResults
      ? { includeMapBoxResults: true }
      : undefined;

    const filter: any = {
      ...onlyPropertiesFilter,
      ...onlyCitiesFilter,
      ...onlyCompaniesFilter,
      ...withSubmarketsFilter,
      ...withOwnersFilter,
      ...withMarketsFilter,
      ...withMapBoxResults,
      [field]: searchTerm,
    };

    if (applyMarketFilter) {
      filter.preferredMarkets = marketsFilter.map(m => m.id);
    }

    return filter;
  };

  const getSearchVariables = (page = 1) => {
    return {
      search: {
        page: {
          page: page,
          size: PAGE_SIZE,
        },
        filter: getFilterQuery(),
      },
    };
  };

  const fetchAdditionalData = async (term: string) => {
    if (!suggestGlobalBoundaries) return [];

    const searchFilters: IBoundarySuggestionInputs = {
      search: term,
      geohierarchyLevels: ['stat1', 'stat2', 'adm1', 'ayoff1', 'ayind1'],
    };

    if (selectedCountry) {
      searchFilters.countryCode = selectedCountry;
    }

    const globalBoundaries =
      (await suggestGlobalBoundaries(
        globalBoundariesSuggestionsUrl,
        searchFilters,
      )) ?? [];

    setAdditionalData(globalBoundaries);
    return globalBoundaries;
  };

  const { data, loading } = useQuery<
    IPropertiesCompaniesSearchResult<SearchResultDataType>
  >(query, {
    fetchPolicy: 'no-cache',
    variables: getSearchVariables(),
    skip: skipQuery || !searchInputText.length,
    onCompleted: async data => {
      let globalData: SearchResultDataType[] = [];

      if (fetchGlobalBoundaries) {
        globalData = await fetchAdditionalData(searchTerm);
      }

      const mapBoxResult = parseMapboxResult
        ? parseMapboxResult(data?.[resultDataName]?.mapBoxResults ?? [])
        : data?.[resultDataName]?.mapBoxResults;

      const results = [
        ...(fetchGlobalBoundaries ? globalData : []),
        ...(data?.[resultDataName]?.results ?? []),
        ...(includeMapBoxResults ? mapBoxResult ?? [] : []),
      ];

      setCombinedResults(results);
    },
  });

  useEffect(() => {
    setSearchInputText('');
  }, [pathname]);

  useEffect(() => {
    setSearchInputText(initialValue || '');
  }, [initialValue]);

  const filteredSuggestions: SearchResultDataType[] =
    !loading && combinedResults?.length
      ? filterSuggestions
        ? filterSuggestions(combinedResults)
        : combinedResults
      : [];

  const specialCharsString = '()[]{}';

  const checkForSpecialChar = function(string: string) {
    return SPECIAL_CHARS.test(string);
  };

  const onChange = (e: any) => {
    const userInput = e.currentTarget.value;
    if (checkForSpecialChar(userInput)) {
      setShowErrorCharacter(true);
      setSkipQuery(true);
    } else {
      if (userInput.length >= MIN_CHARS_FOR_SEARCH) {
        setSkipQuery(false);
      } else {
        setSkipQuery(true);
      }
      setShowErrorCharacter(false);
    }
    setActiveSuggestionIndex(null);
    setShowSuggestions(userInput.length >= MIN_CHARS_FOR_SEARCH);
    setSearchInputText(userInput || '');
    onChangeText?.(userInput || '');
  };

  const clearSearchInput = () => {
    setShowSuggestions(false);
    setActiveSuggestionIndex(null);
    setSearchInputText('');
    onChangeText?.('');
    onClearSelectedSuggestion?.();
  };

  const onSuggestionSelect = (suggestion: SearchResultDataType) => {
    const suggestionParsed = getSuggestionParsed(suggestion);
    setSearchInputText(suggestionParsed?.name || '');
    setShowSuggestions(false);
    setInputIsFocused(false);
    onClickSuggestion(suggestion);

    if (clearAfterSuggestionClick) {
      clearSearchInput();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const allKeysHandled = [
      Keyboard.Enter,
      Keyboard.UpArrow,
      Keyboard.DownArrow,
    ];

    if (allKeysHandled.includes(e.keyCode)) {
      e.preventDefault();
    }

    if (e.keyCode === Keyboard.Enter) {
      if (!showSuggestions) return;

      if (hideEmptySuggestionsPressEnter && !filteredSuggestions.length) {
        setShowSuggestions(false);
      }

      if (activeSuggestionIndex == null) {
        setActiveSuggestionIndex(0);
        return;
      }

      const selectedSuggestion =
        filteredSuggestions[activeSuggestionIndex || 0];
      if (!!selectedSuggestion) {
        onSuggestionSelect(selectedSuggestion);
      }
      return;
    }

    // User pressed the up arrow, decrement the index
    if (e.keyCode === Keyboard.UpArrow) {
      if (!activeSuggestionIndex) {
        return;
      }

      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    }
    // User pressed the down arrow, increment the index
    else if (e.keyCode === Keyboard.DownArrow) {
      if (activeSuggestionIndex === null) {
        setActiveSuggestionIndex(0);
      } else {
        if (activeSuggestionIndex - 1 === filteredSuggestions.length) {
          return;
        }
        setActiveSuggestionIndex(activeSuggestionIndex + 1);
      }
    }
  };

  const renderNoResultsMessage = () => {
    if (
      loading ||
      filteredSuggestions.length > 0 ||
      searchTerm !== searchInputText
    ) {
      return null;
    }

    return (
      <p className={styles['results-feedback']}>
        {translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.notFound`)}
      </p>
    );
  };

  const renderLoadingSpinner = () => {
    if (!loading) {
      return null;
    }

    return <LoadingMessage />;
  };

  const renderSuggestionItem = (
    suggestion: SearchResultDataType,
    index: number,
  ) => (
    <SearchInputSuggestion
      key={index}
      index={index}
      aliasPlaceholder={aliasPlaceholder}
      parseResults={parseResults}
      suggestionIconType={suggestionIconType}
      suggestion={suggestion}
      onSelect={onSuggestionSelect}
      searchInputText={searchInputText}
      activeIndex={activeSuggestionIndex}
      onActiveIndexChange={setActiveSuggestionIndex}
      globalBoundaries={globalBoundaries}
    />
  );

  const renderCustomOption = (hasSuggestions: boolean) => (
    <div className={classnames(styles['custom-option-container'])}>
      <div
        onMouseEnter={() => {
          setActiveSuggestionIndex(null);
        }}
        onMouseDown={() => {
          setSearchInputText(searchInputText);
          setShowSuggestions(false);
          customOption?.onSelection(searchInputText);
        }}
        className={classnames(styles['search-suggestion-item'])}
      >
        <div
          className={classnames(
            styles['suggestion-value'],
            styles['custom-option'],
            { [styles['with-border']]: hasSuggestions },
          )}
        >
          <p className={styles['suggestion-text-primary']}>
            <Icon
              width={0.75}
              height={0.75}
              name="company"
              className={styles.icon}
              color={colors.ayGrey64Color}
            />
            <span>{searchInputText}</span>
          </p>
          {/* TODO: onClick event is not accessible in this button and it should.
           it doesn't affect the current behavior but it might be needed in the future */}
          {!!customOption?.btnLabel && (
            <Button label={customOption?.btnLabel} type="ghost" size="s" />
          )}
        </div>
      </div>
    </div>
  );
  const results = [
    ...(fetchGlobalBoundaries ? additionalData ?? [] : []),
    ...(data?.[resultDataName]?.results ?? []),
    ...(includeMapBoxResults
      ? data?.[resultDataName]?.mapBoxResults ?? []
      : []),
  ];

  const renderSuggestions = () => {
    if (!showSuggestions || !isInputFocused) {
      return null;
    }

    const suggestionItems =
      !loading && filteredSuggestions.length > 0 ? (
        <>{filteredSuggestions.map(renderSuggestionItem)}</>
      ) : null;

    const shouldShowCustomOption =
      customOption?.onSelection && !loading && results !== undefined;

    return (
      <>
        <div
          className={classnames({
            [styles['separator-line']]: !navbar,
          })}
        />
        {renderLoadingSpinner()}
        {showErrorCharacter && (
          <p>
            {`${translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.searchNotAllowedCharacters`,
            )}: ${specialCharsString.split('').join(' ')}`}
          </p>
        )}
        <ul
          className={classnames(styles['search-suggestions'], {
            [styles['search-suggestions-hidden']]: !showSuggestions,
            [styles['with-custom-option']]: shouldShowCustomOption,
          })}
        >
          {!shouldShowCustomOption && renderNoResultsMessage()}
          {suggestionItems}
        </ul>
        {shouldShowCustomOption && renderCustomOption(!!suggestionItems)}
      </>
    );
  };

  const renderInput = () => {
    if (
      isEditable &&
      !isInputFocused &&
      searchInputText.length !== 0 &&
      resultSelectedComponent
    ) {
      return (
        <div
          className={classnames(
            styles['search-input'],
            styles[`search-input-${size}`],
          )}
          onBlur={() => {
            setTimeout(() => {
              setInputIsFocused(false);
            });
          }}
          onClick={() => {
            setTimeout(() => {
              setInputIsFocused(true);
              setShowSuggestions(true);
              inputRef.current?.focus?.();
            });
          }}
        >
          {resultSelectedComponent}
        </div>
      );
    }

    return (
      <input
        ref={inputRef}
        autoFocus={autofocus && !navbar}
        onFocus={() => {
          setInputIsFocused(true);
          setShowSuggestions(searchInputText.length >= MIN_CHARS_FOR_SEARCH);
          onFocus?.();
        }}
        onBlur={() => {
          setInputIsFocused(false);
          onBlur?.();
        }}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={searchInputText}
        autoComplete="off"
        autoCorrect="off"
        disabled={isDisabled}
        placeholder={isDisabled ? DASH_PLACEHOLDER : placeholder}
        className={classnames(
          styles['search-input'],
          styles[`search-input-${size}`],
          {
            [styles['selectable']]: selectable,
            [styles['search-input-icon']]: isSearchIconLeftVisible,
            [styles['search-input-disabled']]: isDisabled,
          },
        )}
        type="text"
        id={inputId || 'search-input'}
        data-testid={inputId}
      />
    );
  };

  const renderButtonClear = () => {
    if (!showSuggestions || !searchInputText.length) {
      return null;
    }

    return (
      <button
        className={classnames(styles['clear-search'])}
        style={{ marginRight: `${clearButtonMarginRight}px` }}
        type="button"
        onClick={clearSearchInput}
      >
        <img
          src={closeIcon}
          alt={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.clear`)}
        />
      </button>
    );
  };

  const isEditableWithSelectedValue = () => {
    return (
      isEditable &&
      !isInputFocused &&
      searchInputText.length >= MIN_CHARS_FOR_SEARCH &&
      !showSuggestions
    );
  };

  const renderEditButton = () => {
    if (isEditableWithSelectedValue()) {
      return (
        <Button
          type="ghost"
          icon="edit"
          onClick={() => {
            setShowSuggestions(true);
            setInputIsFocused(true);
            setTimeout(() => {
              inputRef.current?.focus?.();
            });
          }}
          wrapperClassName={styles['editable-button']}
        />
      );
    }

    return null;
  };

  const renderMarketSwitch = () => {
    if (!withMarketSwitch || isEditableWithSelectedValue()) return null;

    return (
      <div
        ref={marketSwitchContainerRef}
        className={styles['market-switch-container']}
      >
        <MarketSwitch
          selectedMarkets={marketsFilter}
          onChange={newMarkets => setMarketsFilter(newMarkets as IdName[])}
          isDisabled={isDisabled}
        />
      </div>
    );
  };

  return (
    <div
      className={classnames(
        styles['search-input-wrapper'],
        styles[`search-input-wrapper-${size}`],
        {
          [styles[
            'search-input-wrapper-editable'
          ]]: isEditableWithSelectedValue(),
          [styles['search-input-wrapper-focused']]: isInputFocused,
        },
        wrapperClassName,
      )}
    >
      <div
        className={classnames(
          styles['suggestions-container'],
          {
            [styles['suggestions-container-active']]: isInputFocused && !navbar,
            [styles['suggestions-container-navbar']]: navbar,
            [styles['suggestions-container-page-bottom']]: hasBottomMargin,
            [styles['suggestions-container-disabled']]: isDisabled,
          },
          suggestionClassName,
        )}
      >
        <div
          className={classnames(
            styles['input-container'],
            inputContainerClassName,
          )}
        >
          {renderInput()}
          {renderMarketSwitch()}
          {renderButtonClear()}
          {inputBottomChildren}
        </div>

        {renderEditButton()}
        {renderSuggestions()}
      </div>
    </div>
  );
};

export default SearchInput;
